import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../../context/AuthContext';
import { ROUTES } from '../../../constants/routes';
import { userHasFn, SUB_APPS } from '../../../utils/rbac';

function authHeader() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

const tabs = ['properties', 'zones', 'groups', 'questions', 'toggles'];

function RiskAdmin() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [tab, setTab] = useState('properties');

  const [templateId, setTemplateId] = useState('');
  const [templates, setTemplates] = useState([]);
  const [zones, setZones] = useState([]);
  const [zoneIdForGroup, setZoneIdForGroup] = useState('');
  const [groups, setGroups] = useState([]);
  const [groupIdForQuestion, setGroupIdForQuestion] = useState('');
  const [questions, setQuestions] = useState([]);
  const [flatQuestions, setFlatQuestions] = useState([]);

  const [properties, setProperties] = useState([]);
  const [togglePropertyId, setTogglePropertyId] = useState('');
  const [toggles, setToggles] = useState([]);
  const [toggleDirty, setToggleDirty] = useState(false);

  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  const [modal, setModal] = useState({ type: null, payload: null });

  useEffect(() => {
    if (user && !userHasFn(user, SUB_APPS.RISK_CHECKLIST, 'admin')) {
      navigate(ROUTES.RISK);
    }
  }, [user, navigate]);

  useEffect(() => {
    (async () => {
      try {
        const tRes = await axios.get('/api/risk/admin/templates', { headers: authHeader() });
        setTemplates(tRes.data);
        const rts = tRes.data.find((t) => t.slug === 'risk-checklist') || tRes.data[0];
        if (rts) setTemplateId(String(rts.id));
      } catch (e) {
        setError(e.response?.data?.error || e.message);
      }
    })();
  }, []);

  const loadProperties = async () => {
    const res = await axios.get('/api/properties', { headers: authHeader() });
    setProperties(res.data);
  };

  const loadZones = async () => {
    if (!templateId) return;
    const res = await axios.get('/api/risk/admin/zones', {
      headers: authHeader(),
      params: { templateId },
    });
    setZones(res.data);
  };

  const loadGroups = async () => {
    if (!zoneIdForGroup) {
      setGroups([]);
      return;
    }
    const res = await axios.get('/api/risk/admin/groups', {
      headers: authHeader(),
      params: { zoneId: zoneIdForGroup },
    });
    setGroups(res.data);
  };

  const loadQuestions = async () => {
    if (!groupIdForQuestion) {
      setQuestions([]);
      return;
    }
    const res = await axios.get('/api/risk/admin/questions', {
      headers: authHeader(),
      params: { groupId: groupIdForQuestion },
    });
    setQuestions(res.data);
  };

  const loadFlatQuestions = async () => {
    if (!templateId) return;
    const res = await axios.get('/api/risk/admin/questions/flat', {
      headers: authHeader(),
      params: { templateId },
    });
    setFlatQuestions(res.data);
  };

  const loadToggles = async () => {
    if (!togglePropertyId || !templateId) {
      setToggles([]);
      return;
    }
    const res = await axios.get(
      `/api/risk/admin/properties/${togglePropertyId}/toggles`,
      { headers: authHeader(), params: { templateId } }
    );
    setToggles(res.data.map((r) => ({ ...r, enabled: r.enabled })));
    setToggleDirty(false);
  };

  useEffect(() => {
    if (tab === 'properties' || tab === 'toggles') {
      loadProperties().catch((e) => setError(e.message));
    }
  }, [tab]);

  useEffect(() => {
    if (tab === 'zones' || tab === 'groups' || tab === 'questions' || tab === 'toggles') {
      loadZones().catch(() => {});
    }
  }, [tab, templateId]);

  useEffect(() => {
    if (tab === 'groups') loadGroups().catch(() => {});
  }, [tab, zoneIdForGroup]);

  useEffect(() => {
    if (tab === 'questions') {
      loadGroups().catch(() => {});
      loadFlatQuestions().catch(() => {});
    }
  }, [tab, templateId, zoneIdForGroup]);

  useEffect(() => {
    if (tab === 'questions') loadQuestions().catch(() => {});
  }, [tab, groupIdForQuestion]);

  useEffect(() => {
    if (tab === 'toggles') loadToggles().catch(() => {});
  }, [tab, togglePropertyId, templateId]);

  const saveToggles = async () => {
    setError('');
    try {
      await axios.patch(
        `/api/risk/admin/properties/${togglePropertyId}/toggles`,
        {
          toggles: toggles.map((t) => ({
            questionId: t.question_id,
            enabled: t.enabled,
          })),
        },
        { headers: authHeader() }
      );
      setMsg('Toggles saved');
      setToggleDirty(false);
      setTimeout(() => setMsg(''), 2000);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const setToggle = (questionId, enabled) => {
    setToggles((prev) =>
      prev.map((t) => (t.question_id === questionId ? { ...t, enabled } : t))
    );
    setToggleDirty(true);
  };

  if (!user || !userHasFn(user, SUB_APPS.RISK_CHECKLIST, 'admin')) {
    return <p className="text-muted">Admin only.</p>;
  }

  return (
    <div className="app-shell-page">
      <div className="app-page-header mb-4">
        <h2 className="h5 mb-1">Risk checklist admin</h2>
        <p className="text-muted small mb-0">Templates, zones, groups, questions, and per-property toggles.</p>
      </div>
      {error && <div className="alert alert-danger py-2">{error}</div>}
      {msg && <div className="alert alert-success py-2">{msg}</div>}

      <div className="card app-card">
        <div className="card-body">
      <div className="btn-group flex-wrap mb-4">
        {tabs.map((t) => (
          <button
            key={t}
            type="button"
            className={`btn btn-sm ${tab === t ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => {
              setTab(t);
              setError('');
            }}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {tab === 'properties' && (
        <PropertiesSection properties={properties} setError={setError} setModal={setModal} />
      )}

      {tab === 'zones' && (
        <ZonesSection
          templateId={templateId}
          setTemplateId={setTemplateId}
          templates={templates}
          zones={zones}
          onReload={loadZones}
          setError={setError}
          setModal={setModal}
        />
      )}

      {tab === 'groups' && (
        <GroupsSection
          zones={zones}
          zoneIdForGroup={zoneIdForGroup}
          setZoneIdForGroup={setZoneIdForGroup}
          groups={groups}
          onReload={loadGroups}
          setError={setError}
          setModal={setModal}
        />
      )}

      {tab === 'questions' && (
        <QuestionsSection
          zones={zones}
          zoneIdForGroup={zoneIdForGroup}
          setZoneIdForGroup={setZoneIdForGroup}
          groups={groups}
          groupIdForQuestion={groupIdForQuestion}
          setGroupIdForQuestion={setGroupIdForQuestion}
          questions={questions}
          flatQuestions={flatQuestions}
          onReload={() => {
            loadQuestions();
            loadFlatQuestions();
          }}
          setError={setError}
          setModal={setModal}
        />
      )}

      {tab === 'toggles' && (
        <div>
          <div className="row g-2 mb-3">
            <div className="col-md-4">
              <label className="form-label small">Property</label>
              <select
                className="form-select form-select-sm"
                value={togglePropertyId}
                onChange={(e) => setTogglePropertyId(e.target.value)}
              >
                <option value="">Select…</option>
                {properties.length === 0 && (
                  <option disabled value="">
                    Load properties from Properties tab first
                  </option>
                )}
                {properties.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4 d-flex align-items-end">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => loadProperties()}
              >
                Refresh property list
              </button>
            </div>
          </div>
          {togglePropertyId && (
            <>
              <div className="app-table-wrap table-responsive" style={{ maxHeight: 480, overflow: 'auto' }}>
                <table className="table table-sm mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Zone / Group</th>
                      <th>Question</th>
                      <th>On</th>
                    </tr>
                  </thead>
                  <tbody>
                    {toggles.map((t) => (
                      <tr key={t.question_id}>
                        <td className="small text-muted">
                          {t.zone_name} / {t.group_name}
                        </td>
                        <td>{t.question_text}</td>
                        <td>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={!!t.enabled}
                            onChange={(e) => setToggle(t.question_id, e.target.checked)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button
                type="button"
                className="btn btn-primary btn-sm mt-2"
                disabled={!toggleDirty}
                onClick={saveToggles}
              >
                Save toggles
              </button>
            </>
          )}
        </div>
      )}

        </div>
      </div>

      <AdminModal
        modal={modal}
        onClose={() => setModal({ type: null, payload: null })}
        templateId={templateId}
        zoneIdForGroup={zoneIdForGroup}
        groupIdForQuestion={groupIdForQuestion}
        onSaved={() => {
          loadZones();
          loadGroups();
          loadQuestions();
          loadFlatQuestions();
          loadProperties();
        }}
        setError={setError}
      />
    </div>
  );
}

function PropertiesSection({ properties, setError, setModal }) {
  return (
    <div>
      <button
        type="button"
        className="btn btn-success btn-sm mb-2"
        onClick={() => setModal({ type: 'property', payload: {} })}
      >
        Add property
      </button>
      <div className="app-table-wrap table-responsive">
        <table className="table table-sm table-striped mb-0">
          <thead className="table-light">
            <tr>
              <th>Name</th>
              <th>Code</th>
              <th>Address</th>
              <th>Active</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {properties.map((p) => (
              <tr key={p.id}>
                <td>{p.name}</td>
                <td>{p.property_code || '—'}</td>
                <td>{p.address || '—'}</td>
                <td>{p.is_active !== false ? 'Yes' : 'No'}</td>
                <td>
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0"
                    onClick={() => setModal({ type: 'property', payload: p })}
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ZonesSection({ templateId, setTemplateId, templates, zones, onReload, setError, setModal }) {
  return (
    <div>
      <div className="mb-2">
        <label className="form-label small">Template</label>
        <select
          className="form-select form-select-sm w-auto d-inline-block ms-2"
          value={templateId}
          onChange={(e) => setTemplateId(e.target.value)}
        >
          {templates.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
        <button type="button" className="btn btn-sm btn-outline-secondary ms-2" onClick={onReload}>
          Refresh
        </button>
        <button
          type="button"
          className="btn btn-success btn-sm ms-2"
          onClick={() => setModal({ type: 'zone', payload: { templateId: Number(templateId) } })}
          disabled={!templateId}
        >
          Add zone
        </button>
      </div>
      <div className="app-table-wrap table-responsive">
        <table className="table table-sm mb-0">
          <thead className="table-light">
            <tr>
              <th>Name</th>
              <th>Order</th>
              <th>Active</th>
              <th />
            </tr>
          </thead>
          <tbody>
          {zones.map((z) => (
            <tr key={z.id}>
              <td>{z.name}</td>
              <td>{z.sort_order}</td>
              <td>{z.is_active ? 'Yes' : 'No'}</td>
              <td>
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0"
                  onClick={() => setModal({ type: 'zone', payload: z })}
                >
                  Edit
                </button>
              </td>
            </tr>
          ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GroupsSection({
  zones,
  zoneIdForGroup,
  setZoneIdForGroup,
  groups,
  onReload,
  setModal,
}) {
  return (
    <div>
      <div className="mb-2">
        <label className="form-label small">Zone</label>
        <select
          className="form-select form-select-sm w-auto d-inline-block ms-2"
          value={zoneIdForGroup}
          onChange={(e) => setZoneIdForGroup(e.target.value)}
        >
          <option value="">Select zone…</option>
          {zones.map((z) => (
            <option key={z.id} value={z.id}>
              {z.name}
            </option>
          ))}
        </select>
        <button type="button" className="btn btn-sm btn-outline-secondary ms-2" onClick={onReload}>
          Refresh
        </button>
        <button
          type="button"
          className="btn btn-success btn-sm ms-2"
          onClick={() =>
            setModal({
              type: 'group',
              payload: { zoneId: Number(zoneIdForGroup) },
            })
          }
          disabled={!zoneIdForGroup}
        >
          Add group
        </button>
      </div>
      <div className="app-table-wrap table-responsive">
        <table className="table table-sm mb-0">
          <thead className="table-light">
            <tr>
              <th>Name</th>
              <th>Order</th>
              <th>Active</th>
              <th />
            </tr>
          </thead>
          <tbody>
          {groups.map((g) => (
            <tr key={g.id}>
              <td>{g.name}</td>
              <td>{g.sort_order}</td>
              <td>{g.is_active ? 'Yes' : 'No'}</td>
              <td>
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0"
                  onClick={() => setModal({ type: 'group', payload: g })}
                >
                  Edit
                </button>
              </td>
            </tr>
          ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function QuestionsSection({
  zones,
  zoneIdForGroup,
  setZoneIdForGroup,
  groups,
  groupIdForQuestion,
  setGroupIdForQuestion,
  questions,
  flatQuestions,
  onReload,
  setModal,
}) {
  return (
    <div>
      <p className="small text-muted">
        Pick a zone, then a group, to add or edit questions in that group. Flat list below shows all
        questions in the template.
      </p>
      <div className="mb-2">
        <label className="form-label small">Zone</label>
        <select
          className="form-select form-select-sm w-auto d-inline-block ms-2"
          value={zoneIdForGroup}
          onChange={(e) => {
            setZoneIdForGroup(e.target.value);
            setGroupIdForQuestion('');
          }}
        >
          <option value="">Select zone…</option>
          {zones.map((z) => (
            <option key={z.id} value={z.id}>
              {z.name}
            </option>
          ))}
        </select>
      </div>
      <div className="mb-2">
        <label className="form-label small">Group</label>
        <select
          className="form-select form-select-sm w-auto d-inline-block ms-2"
          value={groupIdForQuestion}
          onChange={(e) => setGroupIdForQuestion(e.target.value)}
        >
          <option value="">Select group…</option>
          {groups.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </select>
        <button type="button" className="btn btn-sm btn-outline-secondary ms-2" onClick={onReload}>
          Refresh
        </button>
        <button
          type="button"
          className="btn btn-success btn-sm ms-2"
          onClick={() =>
            setModal({
              type: 'question',
              payload: { groupId: Number(groupIdForQuestion) },
            })
          }
          disabled={!groupIdForQuestion}
        >
          Add question
        </button>
      </div>
      <div className="app-table-wrap table-responsive mb-4">
        <table className="table table-sm mb-0">
          <thead className="table-light">
            <tr>
              <th>Question</th>
              <th>Sort</th>
              <th>Active</th>
              <th />
            </tr>
          </thead>
          <tbody>
          {questions.map((q) => (
            <tr key={q.id}>
              <td>{q.question_text}</td>
              <td>{q.sort_order}</td>
              <td>{q.is_active ? 'Yes' : 'No'}</td>
              <td>
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0"
                  onClick={() => setModal({ type: 'question', payload: q })}
                >
                  Edit
                </button>
              </td>
            </tr>
          ))}
          </tbody>
        </table>
      </div>
      <h6 className="text-muted">All questions (template)</h6>
      <div className="app-table-wrap table-responsive" style={{ maxHeight: 240, overflow: 'auto' }}>
        <table className="table table-sm mb-0">
          <thead className="table-light">
            <tr>
              <th>Zone</th>
              <th>Group</th>
              <th>Question</th>
            </tr>
          </thead>
          <tbody>
            {flatQuestions.map((q) => (
              <tr key={q.id}>
                <td className="small">{q.zone_name}</td>
                <td className="small">{q.group_name}</td>
                <td>{q.question_text}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdminModal({ modal, onClose, templateId, zoneIdForGroup, groupIdForQuestion, onSaved, setError }) {
  const type = modal?.type;
  const payload = modal?.payload;
  const [form, setForm] = useState({});

  useEffect(() => {
    setForm(payload ? { ...payload } : {});
  }, [payload, type]);

  if (!type) return null;

  const save = async () => {
    setError('');
    try {
      if (type === 'property') {
        const body = {
          name: form.name,
          address: form.address || null,
          propertyCode: form.property_code || null,
          isActive: form.is_active !== false,
        };
        if (form.id) {
          await axios.put(`/api/properties/${form.id}`, body, { headers: authHeader() });
        } else {
          await axios.post('/api/properties', body, { headers: authHeader() });
        }
      } else if (type === 'zone') {
        if (form.id) {
          await axios.put(
            `/api/risk/admin/zones/${form.id}`,
            {
              name: form.name,
              description: form.description,
              sortOrder: Number(form.sort_order) || 0,
              isActive: form.is_active !== false,
            },
            { headers: authHeader() }
          );
        } else {
          await axios.post(
            '/api/risk/admin/zones',
            {
              templateId: form.templateId || Number(templateId),
              name: form.name,
              description: form.description,
              sortOrder: Number(form.sort_order) || 0,
              isActive: form.is_active !== false,
            },
            { headers: authHeader() }
          );
        }
      } else if (type === 'group') {
        if (form.id) {
          await axios.put(
            `/api/risk/admin/groups/${form.id}`,
            {
              name: form.name,
              description: form.description,
              sortOrder: Number(form.sort_order) || 0,
              isActive: form.is_active !== false,
            },
            { headers: authHeader() }
          );
        } else {
          await axios.post(
            '/api/risk/admin/groups',
            {
              zoneId: form.zoneId || Number(zoneIdForGroup),
              name: form.name,
              description: form.description,
              sortOrder: Number(form.sort_order) || 0,
              isActive: form.is_active !== false,
            },
            { headers: authHeader() }
          );
        }
      } else if (type === 'question') {
        if (form.id) {
          await axios.put(
            `/api/risk/admin/questions/${form.id}`,
            {
              questionText: form.question_text,
              sortOrder: Number(form.sort_order) || 0,
              isActive: form.is_active !== false,
            },
            { headers: authHeader() }
          );
        } else {
          await axios.post(
            '/api/risk/admin/questions',
            {
              groupId: form.group_id || Number(groupIdForQuestion),
              questionText: form.question_text,
              sortOrder: Number(form.sort_order) || 0,
              isActive: form.is_active !== false,
            },
            { headers: authHeader() }
          );
        }
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  return (
    <div className="modal d-block" tabIndex={-1} style={{ background: 'rgba(0,0,0,0.4)' }}>
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{type}</h5>
            <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
          </div>
          <div className="modal-body">
            {type === 'property' && (
              <>
                <div className="mb-2">
                  <label className="form-label">Name</label>
                  <input
                    className="form-control"
                    value={form.name || ''}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  />
                </div>
                <div className="mb-2">
                  <label className="form-label">Property code</label>
                  <input
                    className="form-control"
                    value={form.property_code || ''}
                    onChange={(e) => setForm((f) => ({ ...f, property_code: e.target.value }))}
                  />
                </div>
                <div className="mb-2">
                  <label className="form-label">Address</label>
                  <input
                    className="form-control"
                    value={form.address || ''}
                    onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                  />
                </div>
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="pactive"
                    checked={form.is_active !== false}
                    onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                  />
                  <label className="form-check-label" htmlFor="pactive">
                    Active
                  </label>
                </div>
              </>
            )}
            {(type === 'zone' || type === 'group') && (
              <>
                <div className="mb-2">
                  <label className="form-label">Name</label>
                  <input
                    className="form-control"
                    value={form.name || ''}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  />
                </div>
                <div className="mb-2">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    value={form.description || ''}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  />
                </div>
                <div className="mb-2">
                  <label className="form-label">Sort order</label>
                  <input
                    type="number"
                    className="form-control"
                    value={form.sort_order ?? 0}
                    onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))}
                  />
                </div>
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="zactive"
                    checked={form.is_active !== false}
                    onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                  />
                  <label className="form-check-label" htmlFor="zactive">
                    Active
                  </label>
                </div>
              </>
            )}
            {type === 'question' && (
              <>
                <div className="mb-2">
                  <label className="form-label">Question</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={form.question_text || ''}
                    onChange={(e) => setForm((f) => ({ ...f, question_text: e.target.value }))}
                  />
                </div>
                <div className="mb-2">
                  <label className="form-label">Sort order</label>
                  <input
                    type="number"
                    className="form-control"
                    value={form.sort_order ?? 0}
                    onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))}
                  />
                </div>
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="qactive"
                    checked={form.is_active !== false}
                    onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                  />
                  <label className="form-check-label" htmlFor="qactive">
                    Active
                  </label>
                </div>
              </>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={save}>
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RiskAdmin;
